import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Users,
  MessageCircle,
  BookOpen,
  TrendingUp,
  Heart,
  Award,
  Target,
} from 'lucide-react';
import { SEO } from '../../../shared/components';

// --- Data Structures ---
interface SupportFeature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

interface FAQ {
    question: string;
    answer: string;
}

const parentInvolvement: SupportFeature[] = [
    {
        icon: <FileText className="w-6 h-6 text-accent" />,
        title: "Weekly Progress Reports",
        description: "Detailed weekly reports on your child's CBSE, IGCSE, or IB academic progress, including test scores, homework completion, and areas for improvement.",
    },
    {
        icon: <Users className="w-6 h-6 text-accent" />,
        title: "Parent-Teacher Meetings",
        description: "Regular one-on-one meetings in Al Wakra to discuss your child's performance, learning goals, and strategies for academic success in Qatar.",
    },
    {
        icon: <BookOpen className="w-6 h-6 text-accent" />,
        title: "Parent Workshops",
        description: "Educational workshops on supporting K-12 learning at home, understanding CBSE/IGCSE/IB curricula, and helping with board exam preparation.",
    },
    {
        icon: <MessageCircle className="w-6 h-6 text-accent" />,
        title: "Open Communication Channels",
        description: "Direct communication with teachers via phone, email, and WhatsApp for quick updates about your child's tutoring in Qatar.",
    },
];

const studentSupport: SupportFeature[] = [
    {
        icon: <Target className="w-6 h-6 text-accent" />,
        title: "Academic Counseling",
        description: "Personalized guidance for K-12 students in Al Wakra on subject selection, CBSE/IGCSE/IB curriculum planning, and career path exploration.",
    },
    {
        icon: <BookOpen className="w-6 h-6 text-accent" />,
        title: "Study Skills Workshops",
        description: "Teaching effective study techniques, time management, exam strategies for CBSE/IGCSE/IB boards, and note-taking methods for Qatar students.",
    },
    {
        icon: <TrendingUp className="w-6 h-6 text-accent" />,
        title: "Progress Monitoring",
        description: "Regular assessments and tracking of academic performance in all subjects, with personalized intervention plans for struggling areas.",
    },
    {
        icon: <Heart className="w-6 h-6 text-accent" />,
        title: "Mental Wellbeing Support",
        description: "Creating a positive learning environment in Al Wakra that reduces academic stress and promotes confidence in K-12 students.",
    },
    {
        icon: <Users className="w-6 h-6 text-accent" />,
        title: "Peer Learning Groups",
        description: "Facilitated study groups where Qatar students can collaborate, share knowledge, and learn from each other in CBSE, IGCSE, and IB subjects.",
    },
    {
        icon: <Award className="w-6 h-6 text-accent" />,
        title: "Recognition Program",
        description: "Celebrating academic achievements, improvements, and consistent effort to motivate students toward excellence in board exams.",
    },
];

const testimonials = [
    {
        name: "Reshma Patel",
        role: "Parent of Grade 8 CBSE Student",
        quote: "The parent-teacher meetings at Tuition Point Al Wakra are invaluable. Teachers explain my child's CBSE progress in detail and offer practical suggestions for improvement.",
    },
    {
        name: "Ahmed Mohamed",
        role: "Parent of Grade 5 Student",
        quote: "I appreciate how Tuition Point Qatar involves parents in the learning journey. The weekly reports help me stay informed about my son's progress in mathematics and science.",
    },
];

const faqs: FAQ[] = [
    {
        question: "How often are parent-teacher meetings held at Tuition Point Qatar?",
        answer: "We conduct parent-teacher meetings quarterly in Al Wakra, with additional meetings available upon request. These meetings provide detailed insights into your child's CBSE, IGCSE, or IB academic progress and learning strategies.",
    },
    {
        question: "How do you track student progress in K-12 subjects?",
        answer: "We use regular assessments, homework evaluations, and classroom participation monitoring. Parents receive weekly progress reports and can access real-time updates on their child's performance in CBSE, IGCSE, and IB curricula.",
    },
    {
        question: "What support is available for struggling students in Qatar?",
        answer: "We offer personalized intervention plans, additional tutoring sessions in Al Wakra, modified learning materials, and one-on-one academic counseling to help students overcome challenges in any subject.",
    },
    {
        question: "Do you provide resources for home learning?",
        answer: "Yes, we provide CBSE/IGCSE/IB worksheets, practice tests, study guides, and access to online resources. Parents also receive guidance on how to support learning at home in Qatar.",
    },
    {
        question: "How do you help students manage academic stress?",
        answer: "Our Al Wakra center provides a supportive environment, teaches stress management techniques, offers flexible scheduling, and maintains open communication with parents about board exam preparation and academic pressures.",
    },
];

const SupportCard: React.FC<{ feature: SupportFeature, delay: number }> = ({ feature, delay }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground p-6 animate-slide-up animation-delay-${delay}`}>
        <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mb-4">
            {feature.icon}
        </div>
        <h3 className="text-xl font-display font-bold mb-3">{feature.title}</h3>
        <p className="text-muted-foreground">{feature.description}</p>
    </div>
);

const ParentSupport: React.FC = () => {
    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Parent & Student Support Services - Al Wakra | Tuition Point Qatar"
                description="Comprehensive parent-teacher communication and student support at Tuition Point Al Wakra. Weekly reports, counseling, and academic guidance for CBSE, IGCSE, IB students in Qatar."
                keywords="parent support Qatar, student counseling Al Wakra, parent-teacher meetings Qatar, academic progress reports, student wellbeing Qatar, K-12 support services, CBSE parent involvement, tutoring support Qatar"
            />

            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">
                            Parent & Student Support - Al Wakra, Qatar
                        </h1>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Collaborative approach to K-12 education with comprehensive support for CBSE, IGCSE, and IB students and their families.
                        </p>
                    </div>
                </div>
            </section>

            {/* --------------------- Parent Involvement --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Parent Involvement in Qatar</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            We partner with parents to ensure students succeed in CBSE, IGCSE, and IB curricula
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {parentInvolvement.map((feature, index) => (
                            <SupportCard key={index} feature={feature} delay={(index % 2) + 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- Student Support Services --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Student Support Services - Al Wakra</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Comprehensive academic and wellbeing support for K-12 students in Qatar
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {studentSupport.map((feature, index) => (
                            <SupportCard key={index} feature={feature} delay={(index % 3) + 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- Parent Testimonials --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Parent Testimonials - Qatar</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            What parents say about our support services in Al Wakra
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className={`bg-white rounded-xl shadow-lg text-foreground p-8 animate-slide-up animation-delay-${index + 1}`}>
                                <p className="text-muted-foreground italic text-lg mb-4">
                                    "{testimonial.quote}"
                                </p>
                                <div className="border-t border-border pt-4">
                                    <p className="font-display font-bold">{testimonial.name}</p>
                                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- FAQ Section --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Frequently Asked Questions</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Common questions about parent and student support services
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto space-y-6">
                        {faqs.map((faq, index) => (
                            <div key={index} className={`bg-white rounded-xl shadow-lg text-foreground p-6 animate-slide-up animation-delay-${(index % 3) + 1}`}>
                                <h3 className="text-xl font-display font-bold mb-3 text-primary">{faq.question}</h3>
                                <p className="text-muted-foreground">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- CTA Section --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 animate-slide-up">
                            Experience Comprehensive Support in Al Wakra
                        </h2>
                        <p className="text-lg text-white text-opacity-90 mb-8 animate-slide-up">
                            Join Tuition Point Qatar for collaborative education with full parent involvement and student support
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                            <Link
                                to="/enrollment"
                                className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
                            >
                                Enroll Today
                            </Link>
                            <Link
                                to="/contact"
                                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ParentSupport;
